import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  fetchGames,
  searchGames,
  addToWishlist,
  removeFromWishlist,
  fetchWishlist
} from "../services/api";
import Filters from "../components/Filters";
import { FaAngleRight, FaAngleDoubleRight, FaHeart, FaStar, FaSearch, FaGamepad } from "react-icons/fa";
import { jwtDecode } from "jwt-decode";
import "../styles/Home.css";

const PAGE_SIZE = 12;

function getUserId() {
  const token = localStorage.getItem("token");
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    return decoded.id;
  } catch {
    return null;
  }
}

// Enhanced Loading Skeleton Component
const GameCardSkeleton = () => (
  <div className="game-card skeleton">
    <div className="game-card-img-box skeleton-img"></div>
    <div className="skeleton-content">
      <div className="skeleton-title"></div>
      <div className="skeleton-subtitle"></div>
      <div className="skeleton-rating"></div>
    </div>
  </div>
);

// Enhanced Pagination Component
const Pagination = ({ page, totalPages, setPage }) => {
  const visiblePages = [];
  const maxVisible = 5;
  let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
  let endPage = startPage + maxVisible - 1;
  
  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = Math.max(1, endPage - maxVisible + 1);
  }
  
  for (let i = startPage; i <= endPage; i++) {
    visiblePages.push(i);
  }

  return (
    <div className="pagination">
      <button
        className="page-btn"
        onClick={() => page > 1 && setPage(page - 1)}
        disabled={page <= 1}
        aria-label="Previous page"
      >
        <FaAngleRight style={{ transform: 'rotate(180deg)' }} />
      </button>
      
      {visiblePages.map((p) => (
        <button
          key={p}
          className={p === page ? "page-btn active" : "page-btn"}
          onClick={() => setPage(p)}
        >
          {p}
        </button>
      ))}
      
      <button
        className="page-btn"
        onClick={() => page < totalPages && setPage(page + 1)}
        disabled={page >= totalPages}
        aria-label="Next page"
      >
        <FaAngleRight />
      </button>
      
      <button
        className="page-btn"
        onClick={() => setPage(totalPages)}
        disabled={page >= totalPages}
        aria-label="Last page"
      >
        <FaAngleDoubleRight />
      </button>
    </div>
  );
};

// Enhanced Home Component
const Home = () => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [totalGames, setTotalGames] = useState(null);
  const [wishlist, setWishlist] = useState([]);
  const [searchPerformed, setSearchPerformed] = useState(false);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const searchTerm = params.get("search") || "";

  const token = localStorage.getItem("token");
  const userId = getUserId();

  useEffect(() => {
    loadGames();
    // eslint-disable-next-line
  }, [filters, searchTerm, page]);

  useEffect(() => {
    if (userId && token) {
      fetchWishlist(userId, token)
        .then(items => setWishlist(items.map(item => item.game_id)))
        .catch(() => setWishlist([]));
    }
  }, [userId, token, games]);

  const loadGames = async () => {
    try {
      setLoading(true);
      let data;
      
      if (searchTerm) {
        data = await searchGames(searchTerm, page);
        setSearchPerformed(true);
      } else {
        data = await fetchGames(page, filters);
        setSearchPerformed(false);
      }
      
      setGames(data.results);
      setTotalGames(data.count || data.results.length);
    } catch (err) {
      console.error('Error loading games:', err);
      setGames([]);
      setTotalGames(0);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setPage(1);
    setFilters(newFilters);
  };

  const handleWishlistToggle = async (game) => {
    if (!userId || !token) {
      alert("Please log in to add games to your wishlist!");
      return;
    }
    try {
      if (wishlist.includes(game.id)) {
        await removeFromWishlist(userId, game.id, token);
        setWishlist(wishlist => wishlist.filter(id => id !== game.id));
      } else {
        await addToWishlist(userId, game.id, token);
        setWishlist(wishlist => [...wishlist, game.id]);
      }
    } catch (err) {
      console.error('Error toggling wishlist:', err);
    }
  };

  const isInWishlist = (gameId) => wishlist.includes(gameId);
  const totalPages = Math.max(1, Math.ceil(totalGames / PAGE_SIZE));

  // Render loading skeletons
  if (loading) {
    return (
      <div className="home">
        <Filters onFilterChange={handleFilterChange} />
        <div className="loading-header">
          <div className="loading-spinner"></div>
          <p>Discovering amazing games...</p>
        </div>
        <div className="games-grid">
          {Array.from({ length: PAGE_SIZE }).map((_, index) => (
            <GameCardSkeleton key={index} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="home">
      <Filters onFilterChange={handleFilterChange} />
      
      {/* Search Results Header */}
      {searchPerformed && (
        <div className="search-results-header">
          <div className="search-icon">
            <FaSearch />
          </div>
          <div className="search-info">
            <h2>Search Results for "{searchTerm}"</h2>
            <p>{totalGames} games found</p>
          </div>
        </div>
      )}

      {/* Games Grid */}
      <div className="games-grid">
        {games && games.length > 0 ? (
          games.map((game) => (
            <Link
              key={game.id}
              className="game-card"
              to={`/games/${game.id}`}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <div className="game-card-img-box">
                <img src={game.background_image} alt={game.name} className="game-card-img" />
                <button
                  className={`wishlist-btn${isInWishlist(game.id) ? " active" : ""}`}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleWishlistToggle(game);
                  }}
                  aria-label={isInWishlist(game.id) ? "Remove from Wishlist" : "Add to Wishlist"}
                >
                  <FaHeart />
                </button>
                
                {/* Game Genres Overlay */}
                {game.genres && game.genres.length > 0 && (
                  <div className="game-genres-overlay">
                    {game.genres.slice(0, 2).map(genre => (
                      <span key={genre.id} className="genre-badge">
                        {genre.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="game-card-content">
                <h3 className="game-title">{game.name}</h3>
                <p className="game-sub">
                  {game.released ? `Released: ${game.released}` : "Release Date: TBA"}
                </p>
                <div className="game-rating-row">
                  <FaStar className="game-star" />
                  <span className="rating-value">{Number(game.rating).toFixed(2)}</span>
                  <span className="game-votes">
                    &middot; {game.ratings_count ? game.ratings_count.toLocaleString() : "0"} votes
                  </span>
                </div>
                
                {/* Platform Information */}
                {game.platforms && game.platforms.length > 0 && (
                  <div className="game-platforms">
                    {game.platforms.slice(0, 3).map(platform => (
                      <span key={platform.id} className="platform-badge">
                        {platform.platform.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </Link>
          ))
        ) : (
          <div className="no-games-found">
            <div className="no-games-icon">
              <FaGamepad />
            </div>
            <h3>No games found</h3>
            <p>
              {searchPerformed 
                ? `No games match your search for "${searchTerm}". Try different keywords or browse all games.`
                : "No games available at the moment. Please try again later."
              }
            </p>
            {searchPerformed && (
              <button 
                className="clear-search-btn"
                onClick={() => window.location.href = '/'}
              >
                Clear Search & Browse All Games
              </button>
            )}
          </div>
        )}
      </div>

      {/* Enhanced Pagination */}
      {games.length > 0 && totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            <span>Page {page} of {totalPages}</span>
            <span>{totalGames} total games</span>
          </div>
          <Pagination page={page} totalPages={totalPages} setPage={setPage} />
        </div>
      )}
    </div>
  );
};

export default Home;
